import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

export interface UserInfo {
    name: string;
    role: string;
    email: string;
}

/**
 * Full session record persisted on the device.
 * Keeps the token together with the user info and the login timestamp so the
 * session can be restored after a refresh / browser restart.
 */
interface StoredSession {
    token: string;
    user: UserInfo | null;
    /** Epoch millis of the moment the session was created on this device. */
    loggedInAt: number;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly TOKEN_KEY = 'auth_token';
    private readonly USER_KEY = 'auth_user';
    private readonly SESSION_KEY = 'auth_session';

    private userSubject = new BehaviorSubject<UserInfo | null>(null);

    /** Observable of the current user info — components can subscribe to this. */
    user$ = this.userSubject.asObservable();

    constructor(private router: Router) {
        // Restore the session saved on this device so refreshing the page or
        // restarting the browser never logs the user out on its own.
        this.userSubject.next(this.restoreSession());
    }

    /**
     * Sets the authentication token.
     * @param token The JWT token to store.
     */
    setToken(token: string): void {
        if (!token) {
            return;
        }
        localStorage.setItem(this.TOKEN_KEY, token);

        const existing = this.getUser();
        if (!existing || existing.name?.includes('|') || existing.role?.includes('|')) {
            const decoded = this.decodeToken(token);
            if (decoded) {
                const role = decoded.role || decoded.roles || decoded.authorities || '';
                const name = decoded.name || decoded.sub || decoded.username || decoded.userName || 'User';
                const email = decoded.email || decoded.emailId || '';
                this.setUser({ name, role, email });
                return;
            }
        }

        this.persistSession();
    }

    /**
     * Gets the authentication token.
     * @returns The JWT token or null if not found.
     */
    getToken(): string | null {
        const token = localStorage.getItem(this.TOKEN_KEY);
        if (token) {
            return token;
        }
        // Fall back to the full session record persisted on this device.
        return this.readStoredSession()?.token || null;
    }

    /**
     * Removes the authentication token.
     */
    removeToken(): void {
        localStorage.removeItem(this.TOKEN_KEY);
    }

    /**
     * Persists the whole session (token + user + login time) in device storage.
     *
     * localStorage is used on purpose: the session must survive page refreshes,
     * tab closes and browser restarts, and is only cleared by an explicit logout.
     */
    private persistSession(): void {
        try {
            const previous = this.readStoredSession();
            const rawUser = localStorage.getItem(this.USER_KEY);
            const session: StoredSession = {
                token: localStorage.getItem(this.TOKEN_KEY) || '',
                user: rawUser ? (JSON.parse(rawUser) as UserInfo) : previous?.user || null,
                loggedInAt: previous?.loggedInAt || Date.now(),
            };
            localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
        } catch {
            // Device storage can be unavailable (private mode / quota). The token
            // and user keys still keep the session for the current tab.
        }
    }

    /** Reads the session record persisted on this device (if any). */
    private readStoredSession(): StoredSession | null {
        try {
            const raw = localStorage.getItem(this.SESSION_KEY);
            if (!raw) {
                return null;
            }
            const parsed = JSON.parse(raw) as StoredSession;
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    }

    /**
     * Restores the session saved on this device so a refresh / browser restart
     * never logs the user out on its own. Returns the restored user (or null).
     */
    restoreSession(): UserInfo | null {
        try {
            const session = this.readStoredSession();

            // 1. Recover the token when the dedicated key is missing.
            if (!localStorage.getItem(this.TOKEN_KEY) && session?.token) {
                localStorage.setItem(this.TOKEN_KEY, session.token);
            }

            // 2. Recover the user info - from storage or straight from the JWT.
            let user = this.getUser();
            const token = this.getToken();
            if (!user && token) {
                const decoded = this.decodeToken(token);
                if (decoded) {
                    user = this.cleanUser({
                        name: decoded.name || decoded.sub || decoded.username || decoded.userName || 'User',
                        role: decoded.role || decoded.roles || decoded.authorities || '',
                        email: decoded.email || decoded.emailId || '',
                    });
                }
            }

            // 3. Keep the device storage complete for the next visit.
            if (token || user) {
                if (user) {
                    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
                }
                this.persistSession();
            }

            return user;
        } catch {
            // Device storage unavailable - start with no session instead of breaking the app.
            return null;
        }
    }

    /**
     * Cleans raw user data, especially pipe-separated strings from JWT claims (e.g. "5|Avi|employee|employee")
     */
    cleanUser(user: Partial<UserInfo>): UserInfo {
        let name = (user.name || '').trim();
        let role = (user.role || '').trim();
        let email = (user.email || '').trim();

        // Check if any field has pipe delimiters e.g. "5|Avi|employee|employee"
        const pipeSource = [name, role, email].find(s => s && s.includes('|')) || '';
        if (pipeSource) {
            const parts = pipeSource.split('|').map(p => p.trim()).filter(Boolean);
            if (parts.length >= 2) {
                // If first part is a number (e.g. ID "5")
                if (/^\d+$/.test(parts[0])) {
                    name = parts[1] || name;
                    role = parts[2] || role;
                } else {
                    name = parts[0] || name;
                    role = parts[1] || role;
                }
            }
        }

        // In case name still has pipes or numbers
        if (name.includes('|')) {
            const nParts = name.split('|').map(p => p.trim()).filter(Boolean);
            name = nParts.find(p => !/^\d+$/.test(p)) || nParts[0] || 'User';
        }

        return {
            name: name || 'User',
            role: this.formatRole(role),
            email: email.includes('|') ? '' : email
        };
    }

    /**
     * Stores user info (name, role, email) in localStorage and notifies subscribers.
     */
    setUser(user: UserInfo): void {
        const cleaned = this.cleanUser(user);
        localStorage.setItem(this.USER_KEY, JSON.stringify(cleaned));
        this.persistSession();
        this.userSubject.next(cleaned);
    }

    /**
     * Formats role string (removes ROLE_ prefix if present).
     */
    formatRole(role: any): string {
        if (Array.isArray(role)) {
            role = role[0] || '';
        }
        if (typeof role === 'object' && role !== null) {
            role = role.authority || role.role || role.name || '';
        }
        if (typeof role === 'string') {
            return role.replace(/^ROLE_/i, '');
        }
        return '';
    }

    /**
     * Decodes a JWT token safely without throwing errors.
     */
    decodeToken(token: string): any {
        try {
            const parts = token.split('.');
            if (parts.length === 3) {
                const base64Url = parts[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                    atob(base64)
                        .split('')
                        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                        .join('')
                );
                return JSON.parse(jsonPayload);
            }
        } catch {
            return null;
        }
        return null;
    }

    /**
     * Retrieves the stored user info, or decodes from token if available.
     */
    getUser(): UserInfo | null {
        let user: UserInfo | null = null;
        const raw = localStorage.getItem(this.USER_KEY);
        if (raw) {
            try {
                user = JSON.parse(raw) as UserInfo;
            } catch {
                // fall through to token decode
            }
        }

        if (!user || user.name?.includes('|') || user.role?.includes('|')) {
            const token = this.getToken();
            if (token) {
                const decoded = this.decodeToken(token);
                if (decoded) {
                    const rawRole = decoded.role || decoded.roles || decoded.authorities || '';
                    const rawName = decoded.name || decoded.sub || decoded.username || decoded.userName || 'User';
                    const rawEmail = decoded.email || decoded.emailId || '';
                    user = { name: rawName, role: rawRole, email: rawEmail };
                }
            }
        }

        if (user) {
            const cleaned = this.cleanUser(user);
            if (raw && (user.name?.includes('|') || user.role?.includes('|'))) {
                localStorage.setItem(this.USER_KEY, JSON.stringify(cleaned));
            }
            return cleaned;
        }

        return null;
    }

    /**
     * Checks if the user is authenticated.
     * @returns True if a token exists, false otherwise.
     */
    isAuthenticated(): boolean {
        const token = this.getToken();
        return !!token;
    }

    /**
     * Checks if the currently logged in user has ADMIN role.
     */
    isAdmin(): boolean {
        if (!this.isAuthenticated()) {
            return false;
        }
        const user = this.getUser();
        const role = (user?.role || '').toUpperCase();
        const name = (user?.name || '').toLowerCase();
        return role.includes('ADMIN') || name === 'admin';
    }

    /**
     * Checks if the currently logged in user has EMPLOYEE role.
     */
    isEmployee(): boolean {
        if (!this.isAuthenticated()) {
            return false;
        }
        const user = this.getUser();
        const role = (user?.role || '').toUpperCase();
        return role.includes('EMPLOYEE');
    }

    /**
     * Checks if the currently logged in user has CLIENT role.
     */
    isClient(): boolean {
        if (!this.isAuthenticated()) {
            return false;
        }
        const user = this.getUser();
        const role = (user?.role || '').toUpperCase();
        return role.includes('CLIENT') || (!this.isAdmin() && !this.isEmployee());
    }

    /**
     * Logs out the user and redirects to login.
     */
    logout(): void {
        // An explicit logout is the only path that clears the device session.
        this.removeToken();
        localStorage.removeItem(this.USER_KEY);
        localStorage.removeItem(this.SESSION_KEY);
        this.userSubject.next(null);
        this.router.navigate(['/auth/login']);
    }
}


